// Starting frames for the reporting prototype. A report begins from one frame
// — the base table its op-chain (pipeline build) or ViewConfig (flat build) runs
// against. Today there are two, both mocked (no backend reads): `deal` (one row
// per deal) and `deal_activity` (one row per logged activity event).
//
// A FrameDef is the single source of truth for everything downstream that used
// to be hardcoded to the deal: the rows to query, the field roles the builder
// offers (group-by dims, numeric measures, date buckets, filters), the named
// measures, column labels, group ordering, and the trend date. The flat engine
// and builder read all of this from the frame, so neither knows about "deals"
// specifically.
import { ACTIVITY_DATEF, MOCK_DEAL_ACTIVITY } from './mock-activity'
import { MOCK_DEALS, STAGE_ORDER } from './mock-data'
import {
  CAT_DIMS,
  DATEF,
  FIELDS,
  materialize,
  NUMF,
  ROWS,
} from './pipeline-data'
import { FieldSchema, FrameId, Row } from './pipeline-types'
import { Metric } from './view-config'

export type { FrameId }

export interface FrameDef {
  id: FrameId
  /** Human label for the frame picker. */
  label: string
  /** One-line description of the grain (what a row represents). */
  description: string
  /** Materialized scalar rows for the pipeline engine (arrays dropped). */
  rows: Row[]
  /** Raw records for the flat engine (arrays/nulls preserved for list display). */
  records: Record<string, unknown>[]
  /** Inferred field schema (column name + type, distinct values for cat cols). */
  fields: FieldSchema[]
  /** ISO date columns — bucketable in groupby, offered as recency filters. */
  dateFields: string[]
  /** Numeric columns offered as aggregate measures (sum/avg/min/max) + filters. */
  numFields: string[]
  /** Categorical columns offered as groupby dimensions + `in` filters. */
  catDims: string[]
  /** Named measures the flat builder offers (count + curated sums/avgs). */
  measures: Metric[]
  /** Display labels per column (falls back to the raw field name). */
  labels: Record<string, string>
  /** All displayable/exportable columns in canonical order (column-picker source). */
  columns: string[]
  /** Default selected columns for a new list report. */
  defaultColumns: string[]
  /** Explicit group order for a categorical dimension (else metric-desc). */
  orderings?: Record<string, string[]>
  /** Date field driving the metric trend/sparkline (absent → trend disabled). */
  trendDate?: string
}

// ── deal frame ──────────────────────────────────────────────────────────────

/** Named measures the deal builder offers, in canonical order. */
const DEAL_MEASURES: Metric[] = [
  { agg: 'count', label: 'Deals' },
  { agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' },
  { agg: 'sum', field: 'annual_volume', label: 'Yearly Volume' },
  { agg: 'sum', field: 'monthly_revenue', label: 'Monthly Revenue' },
  { agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' },
  { agg: 'avg', field: 'annual_revenue', label: 'Avg Yearly Revenue' },
  { agg: 'avg', field: 'days_in_stage', label: 'Avg Days in Stage' },
  // avg of is_won (1 won / 0 lost / null open) = Won ÷ closed = win rate.
  { agg: 'avg', field: 'is_won', label: 'Win Rate' },
]

const DEAL_LABELS: Record<string, string> = {
  deal_id: 'Deal ID',
  account_owner: 'Account Owner',
  company_name: 'Company',
  category: 'Type',
  sub_category: 'Category',
  product: 'Product',
  sales_stage: 'Sales Stage',
  days_in_stage: 'Days in Stage',
  days_since_update: 'Days Since Update',
  is_won: 'Won',
  close_date_override: 'Expected Close',
  datetime_closed: 'Closed',
  created: 'Created',
  note_count: 'Notes',
  contact_count: 'Contacts',
  distributor_names: 'Distributors',
  gpo_names: 'GPOs',
  monthly_volume: 'Monthly Volume',
  annual_volume: 'Yearly Volume',
  monthly_revenue: 'Monthly Revenue',
  annual_revenue: 'Yearly Revenue',
}

/** Deal columns users may show or export, in canonical order. */
const DEAL_COLUMNS: string[] = [
  'deal_id',
  'account_owner',
  'company_name',
  'category',
  'sub_category',
  'product',
  'sales_stage',
  'days_in_stage',
  'days_since_update',
  'close_date_override',
  'datetime_closed',
  'created',
  'note_count',
  'contact_count',
  'distributor_names',
  'gpo_names',
  'monthly_volume',
  'annual_volume',
  'monthly_revenue',
  'annual_revenue',
]

const DEAL_DEFAULT_COLUMNS: string[] = [
  'account_owner',
  'company_name',
  'category',
  'product',
  'sales_stage',
  'created',
  'monthly_volume',
  'annual_revenue',
]

// ── deal_activity frame ───────────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<string, string> = {
  id: 'ID',
  company_id: 'Company ID',
  deal_id: 'Deal ID',
  deal_name: 'Deal',
  owner: 'Owner',
  owner_id: 'Owner ID',
  created_at: 'Date',
  event_type: 'Activity',
}

const ACTIVITY_COLUMNS: string[] = [
  'created_at',
  'event_type',
  'deal_name',
  'owner',
  'deal_id',
]

/** Activity events in lifecycle order — reads better than count-desc. */
const ACTIVITY_EVENT_ORDER: string[] = [
  'deal_created',
  'added_note',
  'added_contact',
  'had_call',
  'had_meeting',
  'added_doc',
  'updated_sales_stage',
  'deal_won',
  'deal_lost',
  'churned',
]

const activity = materialize(MOCK_DEAL_ACTIVITY, ACTIVITY_DATEF)

export const FRAMES: Record<FrameId, FrameDef> = {
  deal: {
    id: 'deal',
    label: 'Deals',
    description: 'One row per deal — the deal_reporting view.',
    rows: ROWS,
    records: MOCK_DEALS,
    fields: FIELDS,
    dateFields: DATEF,
    numFields: NUMF,
    catDims: CAT_DIMS,
    measures: DEAL_MEASURES,
    labels: DEAL_LABELS,
    columns: DEAL_COLUMNS,
    defaultColumns: DEAL_DEFAULT_COLUMNS,
    orderings: { sales_stage: STAGE_ORDER },
    trendDate: 'created',
  },
  deal_activity: {
    id: 'deal_activity',
    label: 'Deal activity',
    description: 'One row per logged activity event on a deal.',
    rows: activity.rows,
    records: MOCK_DEAL_ACTIVITY,
    fields: activity.fields,
    dateFields: ACTIVITY_DATEF,
    // Activities are counted, not summed — there is no natural numeric measure.
    numFields: [],
    catDims: ['event_type', 'owner', 'deal_name'],
    measures: [{ agg: 'count', label: 'Activities' }],
    labels: ACTIVITY_LABELS,
    columns: ACTIVITY_COLUMNS,
    defaultColumns: ['created_at', 'event_type', 'deal_name', 'owner'],
    orderings: { event_type: ACTIVITY_EVENT_ORDER },
    trendDate: 'created_at',
  },
}

/** Frames in picker order. */
export const FRAME_LIST: FrameDef[] = [FRAMES.deal, FRAMES.deal_activity]

/** The frame a new report starts from. */
export const DEFAULT_FRAME: FrameId = 'deal'

/** The frame a ViewConfig runs against (defaults to the registry default). */
export const frameOf = (frame: FrameId = DEFAULT_FRAME): FrameDef =>
  FRAMES[frame]
